import { useQuery } from '@tanstack/react-query'
import { Check, PackageSearch, Plus, Search, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { InlineFieldInput } from '@/components/inline-field-group'
import { useQuoteBuilderApi } from '@/features/quote-builder/api/client'
import { productsQueryOptions } from '@/features/quote-builder/catalog/queries'
import { cn, formatMoney } from '@/lib/utils'

import type { PoItemGroup } from './api'
import { blankLine, lineCents, lineValid, pickedLine, type Line } from './invoice-lines'
import { SearchInput } from '@/components/search-input'

type Picker = 'po' | 'catalog' | null

/**
 * The items an invoice bills or a refund takes off: typed, picked from the project's POs, or
 * picked from the catalog at our selling price (USA-378).
 */
export function InvoiceLinesEditor({
  lines,
  onChange,
  submitted,
  poItems,
  positive = false,
  totalLabel = 'Total',
}: {
  lines: Line[]
  onChange: (lines: Line[]) => void
  submitted: boolean
  poItems: PoItemGroup[]
  /** Refunds take off positive amounts only. */
  positive?: boolean
  /** "Subtotal" when shipping and tax follow the items. */
  totalLabel?: string
}) {
  const [picker, setPicker] = useState<Picker>(null)
  const update = (key: number, patch: Partial<Line>) =>
    onChange(lines.map((line) => (line.key === key ? { ...line, ...patch } : line)))
  // A picked item fills the empty first line instead of leaving it blank above.
  const add = (line: Line) => {
    const empty = lines.length === 1 && !lines[0]?.description.trim() && !lines[0]?.unitPrice.trim()
    onChange(empty ? [line] : [...lines, line])
  }
  const totalCents = lines.reduce((sum, line) => sum + (lineCents(line) ?? 0), 0)

  return (
    <fieldset className="grid gap-2">
      <legend className="mb-1.5 text-sm font-medium">Items</legend>
      <div aria-hidden="true" className="hidden grid-cols-[minmax(0,1fr)_5rem_7.5rem_6.5rem_2rem] gap-2 text-xs text-muted-foreground sm:grid">
        <span>Description</span>
        <span>Qty</span>
        <span>Unit price</span>
        <span className="text-right">Amount</span>
        <span />
      </div>
      <ol className="grid gap-3 sm:gap-2">
        {lines.map((line, index) => {
          const amount = lineCents(line)
          const invalid = submitted && !lineValid(line, { positive })
          return (
            <li
              className="grid grid-cols-[5rem_minmax(0,1fr)_auto] items-center gap-2 border-b pb-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_5rem_7.5rem_6.5rem_2rem] sm:border-b-0 sm:pb-0"
              key={line.key}
            >
              <InlineFieldInput
                aria-invalid={invalid && !line.description.trim()}
                aria-label={`Item ${index + 1} description`}
                className="col-span-2 sm:col-span-1"
                onChange={(event) => update(line.key, { description: event.target.value })}
                placeholder="What it bills"
                value={line.description}
              />
              <Button
                aria-label={`Remove item ${index + 1}`}
                className="justify-self-end sm:order-last"
                disabled={lines.length === 1}
                onClick={() => onChange(lines.filter((item) => item.key !== line.key))}
                size="icon"
                type="button"
                variant="ghost"
              >
                <X aria-hidden="true" />
              </Button>
              <InlineFieldInput
                aria-invalid={invalid && !(Number(line.quantity) > 0)}
                aria-label={`Item ${index + 1} quantity`}
                className="tabular-nums"
                inputMode="decimal"
                onChange={(event) => update(line.key, { quantity: event.target.value })}
                value={line.quantity}
              />
              <div className="relative">
                <span aria-hidden="true" className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  $
                </span>
                <InlineFieldInput
                  aria-invalid={invalid && (amount === null || (positive && amount <= 0))}
                  aria-label={`Item ${index + 1} unit price`}
                  className="pl-5 tabular-nums"
                  inputMode="decimal"
                  onChange={(event) => update(line.key, { unitPrice: event.target.value })}
                  placeholder="0.00"
                  value={line.unitPrice}
                />
              </div>
              <span className="text-right text-sm font-medium tabular-nums">
                {amount === null ? '—' : formatMoney(amount / 100)}
              </span>
            </li>
          )
        })}
      </ol>

      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t pt-3">
        <div className="flex flex-wrap gap-1">
          <Button onClick={() => onChange([...lines, blankLine()])} size="sm" type="button" variant="ghost">
            <Plus aria-hidden="true" />
            Add item
          </Button>
          {poItems.length ? (
            <Button
              aria-expanded={picker === 'po'}
              onClick={() => setPicker(picker === 'po' ? null : 'po')}
              size="sm"
              type="button"
              variant={picker === 'po' ? 'secondary' : 'ghost'}
            >
              <PackageSearch aria-hidden="true" />
              From POs
            </Button>
          ) : null}
          <Button
            aria-expanded={picker === 'catalog'}
            onClick={() => setPicker(picker === 'catalog' ? null : 'catalog')}
            size="sm"
            type="button"
            variant={picker === 'catalog' ? 'secondary' : 'ghost'}
          >
            <Search aria-hidden="true" />
            From catalog
          </Button>
        </div>
        <p className="text-sm">
          <span className="mr-3 text-muted-foreground">{totalLabel}</span>
          <span className="text-lg font-semibold tabular-nums">{formatMoney(totalCents / 100)}</span>
        </p>
      </div>

      {picker === 'po' ? <PoItemPicker groups={poItems} lines={lines} onPick={add} /> : null}
      {picker === 'catalog' ? <CatalogPicker lines={lines} onPick={add} /> : null}
    </fieldset>
  )
}

function PickRow({
  title,
  detail,
  price,
  added,
  onPick,
}: {
  title: string
  detail: string
  price: string
  added: boolean
  onPick: () => void
}) {
  return (
    <li>
      <button
        className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-md px-3 py-2 text-left outline-none transition-colors hover:bg-muted/60 focus-visible:ring-[3px] focus-visible:ring-ring/50"
        onClick={onPick}
        type="button"
      >
        <span className="min-w-0">
          <span className="block truncate text-sm">{title}</span>
          <span className="block truncate text-xs text-muted-foreground tabular-nums">{detail}</span>
        </span>
        <span className="flex items-center gap-2 text-sm font-medium tabular-nums">
          {price}
          {added ? <Check aria-label="Added" className="size-4 text-emerald-600" /> : <Plus aria-hidden="true" className="size-4 text-muted-foreground" />}
        </span>
      </button>
    </li>
  )
}

const panel = 'max-h-72 overflow-y-auto rounded-lg border bg-card p-1'

function PoItemPicker({ groups, lines, onPick }: { groups: PoItemGroup[]; lines: Line[]; onPick: (line: Line) => void }) {
  const added = new Set(lines.map((line) => line.description))
  return (
    <div aria-label="Items on the project's POs" className={panel} role="group">
      {groups.map((group) => (
        <section key={group.po_number ?? 'none'}>
          <h4 className="sticky top-0 bg-card px-3 pb-1 pt-2 text-xs font-medium text-muted-foreground">
            {group.po_number ? `PO ${group.po_number}` : 'Not on a PO'}
            {group.supplier ? ` · ${group.supplier}` : ''}
          </h4>
          <ul>
            {group.items.map((item, index) => (
              <PickRow
                added={added.has(item.description)}
                detail={`${Number(item.quantity)} × ${formatMoney(item.unit_price)}`}
                key={index}
                onPick={() => onPick(pickedLine(item.description, item.quantity, item.unit_price))}
                price={formatMoney(Number(item.quantity) * Number(item.unit_price))}
                title={item.description}
              />
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

function CatalogPicker({ lines, onPick }: { lines: Line[]; onPick: (line: Line) => void }) {
  const api = useQuoteBuilderApi()
  const [query, setQuery] = useState('')
  const [search, setSearch] = useState('')
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query.trim()), 250)
    return () => window.clearTimeout(timer)
  }, [query])
  const results = useQuery({
    ...productsQueryOptions(api, { query: search, page: 1, page_size: 10, sort: 'featured' }),
    enabled: search.length > 1,
  })
  const added = new Set(lines.map((line) => line.description))
  return (
    <div className="grid gap-2">
      <label className="relative block">
        <span className="sr-only">Search the catalog</span>
        <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <SearchInput
          autoFocus
          className="pl-9"
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Model, brand or product"
          type="search"
          value={query}
        />
      </label>
      {search.length > 1 ? (
        <ul aria-label="Catalog products" className={cn(panel, 'grid')}>
          {results.isLoading ? <li className="px-3 py-4 text-center text-sm text-muted-foreground">Searching…</li> : null}
          {results.isError ? <li className="px-3 py-4 text-center text-sm text-destructive">Catalog search is unavailable.</li> : null}
          {results.data?.items.map((product) => {
            const description = `${product.manufacturer ?? ''} ${product.model ?? ''} — ${product.title}`.trim()
            const price = product.price?.sell_amount
            return (
              <PickRow
                added={added.has(description)}
                detail={[product.manufacturer, product.model].filter(Boolean).join(' · ')}
                key={product.id}
                onPick={() => onPick(pickedLine(description, 1, price ?? 0))}
                price={price != null ? formatMoney(price) : '—'}
                title={product.title}
              />
            )
          })}
          {results.data?.items.length === 0 ? (
            <li className="px-3 py-4 text-center text-sm text-muted-foreground">No matching products.</li>
          ) : null}
        </ul>
      ) : null}
    </div>
  )
}
